import { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ClipboardPaste, FileUp, FolderOpen, Loader2, XCircle } from "lucide-react";
import { collectResumes } from "@/lib/talentFiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Panel } from "@/components/si/Bits";
import { api, errMsg } from "@/lib/api";
import { extractText } from "@/lib/siFiles";
import { SI } from "@/lib/sourcing";

const MAX = 10 * 1024 * 1024;
const OK = /\.(pdf|docx|doc)$/i;
const notLive = (e) => (e?.response?.status === 404 ? "Internal talent isn't live on the backend yet (spec §72)." : errMsg(e));
const said = (r) => (r?.action === "updated" ? `Updated ${r.candidate?.full_name || "existing person"}` : `Added ${r?.candidate?.full_name || "new person"}`);

const Row = ({ j }) => (
  <div className="flex items-center gap-2 border-t border-[#1f1f23] py-1.5 text-xs" data-testid={`talent-job-${j.key}`}>
    {j.state === "done" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : j.state === "error" ? <XCircle className="h-3.5 w-3.5 text-red-400" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-[#D4AF37]" />}
    <span className="min-w-0 flex-1 truncate text-zinc-200">{j.name}</span>
    <span className={`shrink-0 text-[11px] ${j.state === "error" ? "text-red-300" : j.state === "done" ? "text-emerald-300" : "text-zinc-500"}`} data-testid={`talent-job-${j.key}-status`}>{j.note}</span>
  </div>
);

// §72 Upload many resumes (PDF/DOCX/DOC) or paste one into the shared RPOPRO internal talent pool.
export const TalentUpload = ({ onDone, readOnly }) => {
  const [mode, setMode] = useState("files");
  const [jobs, setJobs] = useState([]);
  const [drag, setDrag] = useState(false);
  const [paste, setPaste] = useState({ name: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [skipped, setSkipped] = useState([]);
  const [reading, setReading] = useState(false);
  const inp = useRef();
  const dir = useRef();
  const upd = (key, x) => setJobs((js) => js.map((j) => (j.key === key ? { ...j, ...x } : j)));
  const take = async (src, isDrop) => {
    setReading(true);
    try {
      const { resumes, skipped: sk } = await collectResumes(src, isDrop);
      setSkipped(sk);
      if (sk.length) toast.warning(`Skipped ${sk.length} item${sk.length > 1 ? "s" : ""} (subfolders or non-resume files). See the list below.`);
      if (!resumes.length && !sk.length) toast.error("No files found.");
      await uploadAll(resumes);
    } finally { setReading(false); }
  };
  const uploadAll = async (list) => {
    const files = [...(list || [])];
    const bad = files.filter((f) => !OK.test(f.name) || f.size > MAX);
    if (bad.length) toast.error(`Skipped ${bad.length} file${bad.length > 1 ? "s" : ""}: use PDF, .docx or .doc up to 10 MB.`);
    const good = files.filter((f) => !bad.includes(f));
    if (!good.length) return;
    if (good.length > 1) toast.info(`Uploading ${good.length} resumes…`);
    const add = good.map((f, i) => ({ key: `${Date.now()}-${i}`, f, name: f.name, state: "busy", note: "Reading…" }));
    setJobs((js) => [...add, ...js]); setBusy(true);
    let n = 0;
    for (const j of add) {
      let text = "";
      if (!/\.doc$/i.test(j.f.name)) { try { text = ((await extractText(j.f)).text || "").trim(); } catch { /* server reads it */ } }
      upd(j.key, { note: "Uploading…" });
      try {
        const fd = new FormData(); fd.append("file", j.f, j.f.name); fd.append("text", text.slice(0, 60000)); fd.append("source", "upload");
        const { data } = await api.post(`${SI}/talent/resumes`, fd);
        upd(j.key, { state: "done", note: said(data) }); n++;
      } catch (e) { upd(j.key, { state: "error", note: notLive(e) }); }
    }
    setBusy(false);
    if (n) { toast.success(`${n} resume${n > 1 ? "s" : ""} saved to internal talent.`); onDone?.(); }
  };
  const savePaste = async () => {
    if (paste.text.trim().length < 80) return toast.error("Paste the full resume text (at least a few lines).");
    setBusy(true);
    try {
      const { data } = await api.post(`${SI}/talent/paste`, { text: paste.text.trim().slice(0, 60000), name: paste.name.trim() || undefined, source: "paste" });
      toast.success(`${said(data)} in internal talent.`); setPaste({ name: "", text: "" }); onDone?.();
    } catch (e) { toast.error(notLive(e)); } finally { setBusy(false); }
  };
  if (readOnly) return null;
  return (
    <Panel title="Add resumes" testid="talent-upload" right={<div className="flex gap-1.5"><Chip active={mode === "files"} onClick={() => setMode("files")} testid="talent-mode-files">Upload files</Chip><Chip active={mode === "paste"} onClick={() => setMode("paste")} testid="talent-mode-paste">Paste text</Chip></div>}>
      {mode === "files" ? <>
        <button onClick={() => inp.current?.click()} disabled={busy || reading} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer, true); }}
          className={`flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed p-8 text-center transition-colors ${drag ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#3f3f46] hover:border-[#D4AF37]/60"}`} data-testid="talent-drop">
          {reading ? <Loader2 className="h-7 w-7 animate-spin text-[#D4AF37]" /> : <FileUp className="h-7 w-7 text-[#D4AF37]" />}
          <span className="text-sm font-medium text-zinc-100">{reading ? "Opening files…" : "Drop resumes, a folder or a ZIP here, or click to choose"}</span>
          <span className="text-xs text-zinc-500">PDF, Word .docx or .doc · up to 10 MB each · top level of folders/ZIPs only</span>
        </button>
        <div className="mt-2 flex justify-center"><Button size="sm" variant="outline" disabled={busy || reading} onClick={() => dir.current?.click()} className="h-8 border-[#27272A] text-zinc-200" data-testid="talent-folder-btn"><FolderOpen className="h-3.5 w-3.5 text-[#D4AF37]" />Choose a folder</Button></div>
        <input ref={inp} type="file" multiple accept=".pdf,.docx,.doc,.zip,application/pdf,application/msword,application/zip,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} data-testid="talent-file" />
        <input ref={dir} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={(e) => { take(e.target.files); e.target.value = ""; }} data-testid="talent-folder" />
        {skipped.length > 0 && <details className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-2 text-xs" data-testid="talent-skipped">
          <summary className="cursor-pointer text-amber-300" data-testid="talent-skipped-summary">Skipped {skipped.length} item{skipped.length > 1 ? "s" : ""}</summary>
          <div className="mt-1 max-h-40 overflow-y-auto">{skipped.map((s, i) => <p key={i} className="flex gap-2 border-t border-[#1f1f23] py-1 text-zinc-400" data-testid={`talent-skipped-${i}`}><span className="min-w-0 flex-1 truncate">{s.name}</span><span className="shrink-0 text-zinc-500">{s.reason}</span></p>)}</div>
        </details>}
        {jobs.length > 0 && <div className="mt-3" data-testid="talent-jobs">{jobs.map((j) => <Row key={j.key} j={j} />)}</div>}
      </> : <div className="space-y-2">
        <Input value={paste.name} onChange={(e) => setPaste({ ...paste, name: e.target.value })} placeholder="Name (optional, we'll read it from the resume)" className="h-9 text-sm" data-testid="talent-paste-name" />
        <Textarea rows={10} value={paste.text} onChange={(e) => setPaste({ ...paste, text: e.target.value })} placeholder="Paste the full resume text here…" className="text-xs" data-testid="talent-paste-text" />
        <Button onClick={savePaste} disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="talent-paste-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardPaste className="h-4 w-4" />}Save to internal talent</Button>
      </div>}
      <p className="mt-3 text-[11px] text-zinc-500">If someone is already in the system we update them with the new resume and keep anything you entered by hand.</p>
    </Panel>
  );
};
